"use client";

import { Link } from "@/i18n/routing";
import { Button, Card, CardBody, Spinner, Tab, Tabs } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import PageLayout from "../layout/PageLayout";
import { PWAInstallGuide } from "@/components/notifications";

// Dynamic imports for heavy components using framer-motion (code-splitting)
const TowerVisualization = dynamic(
  () =>
    import("../how-to/TowerVisualization").then(
      (mod) => mod.TowerVisualization,
    ),
  {
    loading: () => (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    ),
    ssr: false,
  },
);

const SpecialCardsSection = dynamic(
  () =>
    import("../how-to/SpecialCardsSection").then(
      (mod) => mod.SpecialCardsSection,
    ),
  {
    loading: () => (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    ),
    ssr: false,
  },
);

// Step definitions for each role
const playerSteps = [
  {
    key: "browse",
    icon: "game-icons:magnifying-glass",
    color: "text-amber-500",
  },
  {
    key: "createTeam",
    icon: "game-icons:rally-the-troops",
    color: "text-sky-500",
  },
  {
    key: "enterTower",
    icon: "game-icons:stone-tower",
    color: "text-emerald-500",
  },
  { key: "challenge", icon: "game-icons:swords-power", color: "text-rose-500" },
  { key: "play", icon: "game-icons:sword-clash", color: "text-violet-500" },
  { key: "climb", icon: "game-icons:stairs-goal", color: "text-amber-500" },
];

const organizerSteps = [
  { key: "create", icon: "game-icons:crown", color: "text-amber-500" },
  { key: "configure", icon: "game-icons:stone-tower", color: "text-sky-500" },
  {
    key: "locations",
    icon: "game-icons:position-marker",
    color: "text-emerald-500",
  },
  { key: "teams", icon: "majesticons:users", color: "text-rose-500" },
  { key: "monitor", icon: "solar:chart-2-bold", color: "text-violet-500" },
  { key: "results", icon: "solar:document-add-bold", color: "text-amber-500" },
];

interface SimpleStepCardProps {
  step: number;
  title: string;
  description: string;
  icon: string;
  iconColor: string;
}

function SimpleStepCard({
  step,
  title,
  description,
  icon,
  iconColor,
}: SimpleStepCardProps) {
  return (
    <Card className="bg-content1/80 border border-default-200/30 overflow-hidden">
      <CardBody className="p-4 md:p-5 relative md:min-h-[180px]">
        <Icon
          icon={icon}
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-7xl md:text-8xl ${iconColor}`}
        />
        <div className="flex gap-4 relative z-10 pr-16 md:pr-24">
          <div className="flex-shrink-0">
            <span className="font-cinzel font-bold text-2xl md:text-3xl text-[var(--gold)]">
              {step}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-base md:text-lg text-[var(--text-primary)] mb-2">
              {title}
            </h4>
            <p className="text-sm md:text-base text-default-500 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export function HowItWorksClient() {
  const t = useTranslations();

  return (
    <PageLayout
      pageHeaderProps={{
        pretitle: t("howItWorks.subtitle"),
        title: t("howItWorks.title"),
      }}
    >
      <div className="flex flex-col gap-10 md:gap-14 w-full pb-16">
        {/* ============================================================== */}
        {/* TABBED STEP-BY-STEP FLOW */}
        {/* ============================================================== */}
        <section>
          <Tabs
            aria-label={t("howItWorks.title")}
            variant="light"
            size="lg"
            classNames={{
              base: "w-full",
              tabList:
                "gap-2 md:gap-4 w-full p-1.5 md:p-2 bg-content1/50 rounded-2xl border border-default-200/30 flex-nowrap",
              cursor:
                "bg-gradient-to-br from-[var(--gold)]/20 to-amber-500/10 border border-[var(--gold)]/40 shadow-lg shadow-[var(--gold)]/10",
              tab: "h-auto px-3 py-3 md:px-6 md:py-4 data-[hover-unselected=true]:bg-default-100/50 rounded-xl transition-colors flex-1 min-w-0",
              tabContent: "group-data-[selected=true]:text-[var(--gold)]",
              panel: "pt-8",
            }}
          >
            <Tab
              key="player"
              title={
                <div className="flex items-center gap-2 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0 group-data-[selected=true]:bg-emerald-500/20 group-data-[selected=true]:border-emerald-500/40 transition-colors">
                    <Icon
                      icon="game-icons:crossed-swords"
                      className="text-xl md:text-3xl text-emerald-500"
                    />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-cinzel font-bold text-sm md:text-xl tracking-wide truncate">
                      {t("howItWorks.player.title")}
                    </span>
                    <span className="text-xs md:text-sm text-default-500 hidden sm:block">
                      {t("howItWorks.player.subtitle")}
                    </span>
                  </div>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {playerSteps.map((step, index) => (
                  <SimpleStepCard
                    key={step.key}
                    step={index + 1}
                    title={t(`howItWorks.playerSteps.${step.key}.title`)}
                    description={t(
                      `howItWorks.playerSteps.${step.key}.description`,
                    )}
                    icon={step.icon}
                    iconColor={step.color}
                  />
                ))}
              </div>
            </Tab>
            <Tab
              key="organizer"
              title={
                <div className="flex items-center gap-2 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0 group-data-[selected=true]:bg-amber-500/20 group-data-[selected=true]:border-amber-500/40 transition-colors">
                    <Icon
                      icon="game-icons:crown"
                      className="text-xl md:text-3xl text-amber-500"
                    />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className="font-cinzel font-bold text-sm md:text-xl tracking-wide truncate">
                      {t("howItWorks.organizer.title")}
                    </span>
                    <span className="text-xs md:text-sm text-default-500 hidden sm:block">
                      {t("howItWorks.organizer.subtitle")}
                    </span>
                  </div>
                </div>
              }
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {organizerSteps.map((step, index) => (
                  <SimpleStepCard
                    key={step.key}
                    step={index + 1}
                    title={t(`howItWorks.organizerSteps.${step.key}.title`)}
                    description={t(
                      `howItWorks.organizerSteps.${step.key}.description`,
                    )}
                    icon={step.icon}
                    iconColor={step.color}
                  />
                ))}
              </div>
            </Tab>
          </Tabs>
        </section>

        {/* ============================================================== */}
        {/* TOWER VISUALIZATION */}
        {/* ============================================================== */}
        <section>
          <TowerVisualization />
        </section>

        {/* ============================================================== */}
        {/* SPECIAL CARDS SECTION */}
        {/* ============================================================== */}
        <section>
          <SpecialCardsSection />
        </section>

        {/* ============================================================== */}
        {/* INSTALL APP SECTION */}
        {/* ============================================================== */}
        <section>
          <div className="relative text-center mb-8 md:mb-10">
            <h2 className="text-xl md:text-2xl font-bold font-cinzel tracking-wide text-[var(--text-primary)]">
              {t("pwaInstall.title")}
            </h2>
            <div className="absolute left-1/2 -bottom-3 h-0.5 w-16 md:w-20 -translate-x-1/2 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
          </div>
          <PWAInstallGuide className="max-w-2xl mx-auto" />
        </section>

        {/* ============================================================== */}
        {/* CALL TO ACTION */}
        {/* ============================================================== */}
        <section className="text-center py-8 md:py-12">
          <div className="relative inline-block mb-6">
            <h2 className="text-2xl md:text-3xl font-bold font-cinzel tracking-wide bg-gradient-to-r from-[var(--text-primary)] to-[var(--gold)] bg-clip-text text-transparent">
              {t("howItWorks.cta.ready")}
            </h2>
            <div className="absolute -bottom-2 left-1/2 h-0.5 w-24 -translate-x-1/2 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" />
          </div>

          <p className="text-default-500 mb-8 max-w-lg mx-auto text-sm md:text-base">
            {t("howItWorks.cta.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              as={Link}
              href="/tournaments"
              color="primary"
              size="lg"
              className="font-bold px-8 shadow-lg shadow-primary/25"
              startContent={
                <Icon icon="solar:cup-star-bold" className="text-xl" />
              }
            >
              {t("howItWorks.cta.browseTournaments")}
            </Button>
            <Button
              as={Link}
              href="/start"
              variant="bordered"
              size="lg"
              className="font-bold px-8 border-[var(--gold)]/50 text-[var(--gold)]"
              startContent={
                <Icon icon="solar:add-circle-bold" className="text-xl" />
              }
            >
              {t("howItWorks.cta.createTournament")}
            </Button>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
